import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { ThemeProvider } from '@/context/ThemeContext';
import { LayoutProvider } from '@/context/LayoutContext';
import { api } from '@/services/api';
import { initMockData } from '@/utils/mockData';
import { supabase } from '@/services/supabase';

// Import the new AuthProvider instead of the old one
import { AuthProviderV2 } from '@/context/auth-v2/AuthContext';

// Policy Pages
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsAndConditions from '@/pages/TermsAndConditions';

// Auth Pages - Use the new V2 versions
import LoginV2 from '@/pages/auth/Login';
import RegisterV2 from '@/pages/auth/Register';
import { WaitingApproval } from '@/pages/auth/WaitingApproval';
import { SuperLoginPage } from './pages/auth/SuperLogin';
import SuperRegister from './pages/auth/SuperRegister';
import Callback from './pages/auth/Callback';
import Validate from './pages/auth/Validate';
import AcceptInvitation from './pages/auth/AcceptInvitation';
import ForgotPasswordPage from './pages/auth/ForgotPassword';
import ResetPasswordPage from './pages/auth/ResetPassword';

// Dashboard Pages
import Dashboard from '@/pages/dashboard/Dashboard';
import CharityDashboard from '@/pages/dashboard/CharityDashboard';

// Business Pages
import ProductsList from '@/pages/products/ProductsList';
import AddProduct from '@/pages/products/AddProduct';
import BulkUpload from '@/pages/products/BulkUpload';
import Orders from '@/pages/orders/Orders';
import OrderDetails from '@/pages/orders/OrderDetails';
import DonationRequests from '@/pages/orders/DonationRequests';
import BusinessUsers from '@/pages/business/BusinessUsers';
import BusinessSettings from '@/pages/settings/BusinessSettings';

// Charity Pages
import Donations from '@/pages/charity/Donations';
import RequestDonations from '@/pages/charity/RequestDonations';
import Events from '@/pages/charity/Events';
import AddEvent from '@/pages/charity/AddEvent';
import Volunteers from '@/pages/charity/Volunteers';
import CharityUsers from '@/pages/charity/CharityUsers';
import CharitySettings from './pages/charity/CharitySettings';

// Statistics Pages
import RatingsDashboard from '@/pages/statistics/RatingsDashboard';
import SocialImpactDashboard from '@/pages/statistics/SocialImpactDashboard';
import AnalyticsDashboard from '@/pages/statistics/AnalyticsDashboard';
import RatingsDashboardCharity from '@/pages/statistics/RatingsDashboardCharity';
import SocialImpactDashboardCharity from '@/pages/statistics/SocialImpactDashboardCharity';
import AnalyticsDashboardCharity from '@/pages/statistics/AnalyticsDashboardCharity';

// Settings Pages
import ProfileSettings from './pages/settings/ProfileSettings';
import CharityProfileSettings from './pages/charity/CharityProfileSettings';

// Import Dev Components
import DevStaffManagement from '@/components/dev/DevStaffManagement';
import DevProfileSettings from '@/components/dev/settings/DevProfileSettings';

// Import Layout Components
import BusinessLayoutRoute from '@/components/layout/BusinessLayoutRoute';
import CharityLayoutRoute from '@/components/layout/CharityLayoutRoute';
import DevLayoutBusiness from '@/components/dev/DevLayoutBusiness';
import DevLayoutCharity from '@/components/dev/DevLayoutCharity';

// Import Help Center Routes
import HelpCenter from '@/pages/help/HelpCenter';
import FAQ from '@/pages/help/FAQ';
import ContactSupport from '@/pages/help/ContactSupport';
import ReportIssue from '@/pages/help/ReportIssue';

// Import the new ProtectedRoute component
import ProtectedRouteV2 from '@/components/auth-v2/ProtectedRoute';

// Import user schema helpers

// Helper function to check dev mode status

// Helper function to get dev user data

// Helper function to check if dev mode is active with specific role

/**
 * BusinessRoute component - Handles authentication and authorization for business routes
 * Uses the new ProtectedRouteV2 component
 */
const BusinessRoute = ({ children }: { children: React.ReactNode }) => {
  return (
    <ProtectedRouteV2 requiredRoles={['business']}>
      {children}
    </ProtectedRouteV2>
  );
};

/**
 * CharityRoute component - Handles authentication and authorization for charity routes
 * Uses the new ProtectedRouteV2 component
 */
const CharityRoute = ({ children }: { children: React.ReactNode }) => {
  return (
    <ProtectedRouteV2 requiredRoles={['charity']}>
      {children}
    </ProtectedRouteV2>
  );
};

// Initialize mock data for development mode
// For super-admin, we'll still use real data even in dev routes
// Use an IIFE to handle the async check
(async () => {
  try {
    // Check if user is a super admin directly from Supabase
    const { data } = await supabase.auth.getSession();
    const isSuperAdmin = data?.session?.user?.user_metadata?.role === 'super_admin';
    
    if (!isSuperAdmin) {
      initMockData(api);
    }
  } catch (error) {
    console.error('Error checking superadmin status for mock data:', error);
    // Initialize mock data as fallback
    initMockData(api);
  }
})();

function AppV2() {
  const [, setIsSuperAdmin] = useState<boolean>(false);

  useEffect(() => {
    checkSuperAdmin();
  }, []);

  const checkSuperAdmin = async () => {
    try {
      const { data } = await supabase.auth.getSession();
      setIsSuperAdmin(data?.session?.user?.user_metadata?.role === 'super_admin');
    } catch (error) {
      console.error('Error checking super admin status:', error);
    }
  };

  return (
    <Router>
      <AuthProviderV2>
        <ThemeProvider>
          <LayoutProvider>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Navigate to="/login" />} />
            <Route path="/login" element={<LoginV2 />} />
            <Route path="/register" element={<RegisterV2 />} />
            <Route path="/waiting-approval" element={<WaitingApproval />} />
            <Route path="/super-login" element={<SuperLoginPage />} />
            <Route path="/super-register" element={<SuperRegister />} />
            <Route path="/callback" element={<Callback />} />
            <Route path="/validate" element={<Validate />} />
            <Route path="/accept-invitation" element={<AcceptInvitation />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />

            {/* Business Routes */}
            <Route path="/business/dashboard" element={
              <BusinessRoute>
                <BusinessLayoutRoute title="Dashboard">
                  <Dashboard />
                </BusinessLayoutRoute>
              </BusinessRoute>
            } />

            <Route path="/business/products" element={
              <BusinessRoute>
                <BusinessLayoutRoute title="Products">
                  <ProductsList />
                </BusinessLayoutRoute>
              </BusinessRoute>
            } />

            <Route path="/business/products/add" element={
              <BusinessRoute>
                <BusinessLayoutRoute title="Add Product">
                  <AddProduct />
                </BusinessLayoutRoute>
              </BusinessRoute>
            } />

            <Route path="/business/products/bulk-upload" element={
              <BusinessRoute>
                <BusinessLayoutRoute title="Bulk Upload">
                  <BulkUpload />
                </BusinessLayoutRoute>
              </BusinessRoute>
            } />

            <Route path="/business/orders" element={
              <BusinessRoute>
                <BusinessLayoutRoute title="Orders">
                  <Orders />
                </BusinessLayoutRoute>
              </BusinessRoute>
            } />

            <Route path="/business/orders/:id" element={
              <BusinessRoute>
                <BusinessLayoutRoute title="Order Details">
                  <OrderDetails />
                </BusinessLayoutRoute>
              </BusinessRoute>
            } />

            <Route path="/business/donation-requests" element={
              <BusinessRoute>
                <BusinessLayoutRoute title="Donation Requests">
                  <DonationRequests />
                </BusinessLayoutRoute>
              </BusinessRoute>
            } />

            <Route path="/business/users" element={
              <BusinessRoute>
                <BusinessLayoutRoute title="Users">
                  <BusinessUsers />
                </BusinessLayoutRoute>
              </BusinessRoute>
            } />

            <Route path="/business/statistics" element={
              <BusinessRoute>
                <BusinessLayoutRoute title="Statistics">
                  <RatingsDashboard />
                </BusinessLayoutRoute>
              </BusinessRoute>
            } />

            <Route path="/business/statistics/ratings" element={
              <BusinessRoute>
                <BusinessLayoutRoute title="Ratings & Reviews">
                  <RatingsDashboard />
                </BusinessLayoutRoute>
              </BusinessRoute>
            } />

            <Route path="/business/statistics/social-impact" element={
              <BusinessRoute>
                <BusinessLayoutRoute title="Social Impact">
                  <SocialImpactDashboard />
                </BusinessLayoutRoute>
              </BusinessRoute>
            } />

            <Route path="/business/statistics/analytics" element={
              <BusinessRoute>
                <BusinessLayoutRoute title="Analytics">
                  <AnalyticsDashboard />
                </BusinessLayoutRoute>
              </BusinessRoute>
            } />

            <Route path="/business/settings" element={
              <BusinessRoute>
                <BusinessLayoutRoute title="Settings">
                  <BusinessSettings />
                </BusinessLayoutRoute>
              </BusinessRoute>
            } />

            <Route path="/business/profile" element={
              <BusinessRoute>
                <BusinessLayoutRoute title="Profile Settings">
                  <ProfileSettings />
                </BusinessLayoutRoute>
              </BusinessRoute>
            } />

            {/* Charity Routes */}
            <Route path="/charity/dashboard" element={
              <CharityRoute>
                <CharityLayoutRoute title="Dashboard">
                  <CharityDashboard />
                </CharityLayoutRoute>
              </CharityRoute>
            } />

            <Route path="/charity/users" element={
              <CharityRoute>
                <CharityLayoutRoute title="Users">
                  <CharityUsers />
                </CharityLayoutRoute>
              </CharityRoute>
            } />

            <Route path="/charity/donations" element={
              <CharityRoute>
                <CharityLayoutRoute title="Donations">
                  <Donations />
                </CharityLayoutRoute>
              </CharityRoute>
            } />

            <Route path="/charity/donations/request" element={
              <CharityRoute>
                <CharityLayoutRoute title="Request Donations">
                  <RequestDonations />
                </CharityLayoutRoute>
              </CharityRoute>
            } />

            <Route path="/charity/events" element={
              <CharityRoute>
                <CharityLayoutRoute title="Events">
                  <Events />
                </CharityLayoutRoute>
              </CharityRoute>
            } />

            <Route path="/charity/events/create" element={
              <CharityRoute>
                <CharityLayoutRoute title="Add Event">
                  <AddEvent />
                </CharityLayoutRoute>
              </CharityRoute>
            } />

            <Route path="/charity/volunteers" element={
              <CharityRoute>
                <CharityLayoutRoute title="Volunteers">
                  <Volunteers />
                </CharityLayoutRoute>
              </CharityRoute>
            } />

            <Route path="/charity/statistics" element={
              <CharityRoute>
                <CharityLayoutRoute title="Statistics">
                  <RatingsDashboardCharity />
                </CharityLayoutRoute>
              </CharityRoute>
            } />

            <Route path="/charity/statistics/ratings" element={
              <CharityRoute>
                <CharityLayoutRoute title="Ratings & Reviews">
                  <RatingsDashboardCharity />
                </CharityLayoutRoute>
              </CharityRoute>
            } />

            <Route path="/charity/statistics/social-impact" element={
              <CharityRoute>
                <CharityLayoutRoute title="Social Impact">
                  <SocialImpactDashboardCharity />
                </CharityLayoutRoute>
              </CharityRoute>
            } />

            <Route path="/charity/statistics/analytics" element={
              <CharityRoute>
                <CharityLayoutRoute title="Analytics">
                  <AnalyticsDashboardCharity />
                </CharityLayoutRoute>
              </CharityRoute>
            } />

            <Route path="/charity/settings" element={
              <CharityRoute>
                <CharityLayoutRoute title="Settings">
                  <CharitySettings />
                </CharityLayoutRoute>
              </CharityRoute>
            } />

            <Route path="/charity/profile" element={
              <CharityRoute>
                <CharityLayoutRoute title="Profile Settings">
                  <CharityProfileSettings />
                </CharityLayoutRoute>
              </CharityRoute>
            } />

            {/* Help Center Routes */}
            <Route path="/help" element={
              <ProtectedRouteV2>
                <HelpCenter />
              </ProtectedRouteV2>
            } />

            <Route path="/help/faq" element={
              <ProtectedRouteV2>
                <FAQ />
              </ProtectedRouteV2>
            } />

            <Route path="/help/contact" element={
              <ProtectedRouteV2>
                <ContactSupport />
              </ProtectedRouteV2>
            } />

            <Route path="/help/report" element={
              <ProtectedRouteV2>
                <ReportIssue />
              </ProtectedRouteV2>
            } />

            {/* Dev Routes - Business */}
            <Route path="/dev/business/dashboard" element={
              <DevLayoutBusiness title="Dashboard">
                <Dashboard />
              </DevLayoutBusiness>
            } />
            
            <Route path="/dev/business/users" element={
              <DevLayoutBusiness title="Staff Management">
                <DevStaffManagement orgType="business" />
              </DevLayoutBusiness>
            } />

            <Route path="/dev/business/products" element={
              <DevLayoutBusiness title="Products">
                <ProductsList />
              </DevLayoutBusiness>
            } />

            <Route path="/dev/business/products/add" element={
              <DevLayoutBusiness title="Add Product">
                <AddProduct />
              </DevLayoutBusiness>
            } />

            <Route path="/dev/business/products/bulk-upload" element={
              <DevLayoutBusiness title="Bulk Upload">
                <BulkUpload />
              </DevLayoutBusiness>
            } />

            <Route path="/dev/business/orders" element={
              <DevLayoutBusiness title="Orders">
                <Orders />
              </DevLayoutBusiness>
            } />

            <Route path="/dev/business/donation-requests" element={
              <DevLayoutBusiness title="Donation Requests">
                <DonationRequests />
              </DevLayoutBusiness>
            } />

            <Route path="/dev/business/statistics" element={
              <DevLayoutBusiness title="Statistics">
                <RatingsDashboard />
              </DevLayoutBusiness>
            } />

            <Route path="/dev/business/statistics/ratings" element={
              <DevLayoutBusiness title="Ratings & Reviews">
                <RatingsDashboard />
              </DevLayoutBusiness>
            } />

            <Route path="/dev/business/statistics/social-impact" element={
              <DevLayoutBusiness title="Social Impact">
                <SocialImpactDashboard />
              </DevLayoutBusiness>
            } />

            <Route path="/dev/business/statistics/analytics" element={
              <DevLayoutBusiness title="Analytics">
                <AnalyticsDashboard />
              </DevLayoutBusiness>
            } />

            <Route path="/dev/business/settings" element={
              <DevLayoutBusiness title="Settings">
                <BusinessSettings />
              </DevLayoutBusiness>
            } />

            <Route path="/dev/business/profile" element={
              <DevLayoutBusiness title="Profile Settings">
                <DevProfileSettings orgType="business" />
              </DevLayoutBusiness>
            } />

            {/* Dev Routes - Charity */}
            <Route path="/dev/charity/dashboard" element={
              <DevLayoutCharity title="Dashboard">
                <CharityDashboard />
              </DevLayoutCharity>
            } />
            
            <Route path="/dev/charity/users" element={
              <DevLayoutCharity title="Staff Management">
                <DevStaffManagement orgType="charity" />
              </DevLayoutCharity>
            } />

            <Route path="/dev/charity/donations" element={
              <DevLayoutCharity title="Donations">
                <Donations />
              </DevLayoutCharity>
            } />

            <Route path="/dev/charity/donations/request" element={
              <DevLayoutCharity title="Request Donations">
                <RequestDonations />
              </DevLayoutCharity>
            } />

            <Route path="/dev/charity/events" element={
              <DevLayoutCharity title="Events">
                <Events />
              </DevLayoutCharity>
            } />

            <Route path="/dev/charity/events/create" element={
              <DevLayoutCharity title="Add Event">
                <AddEvent />
              </DevLayoutCharity>
            } />

            <Route path="/dev/charity/volunteers" element={
              <DevLayoutCharity title="Volunteers">
                <Volunteers />
              </DevLayoutCharity>
            } />

            <Route path="/dev/charity/statistics" element={
              <DevLayoutCharity title="Statistics">
                <RatingsDashboardCharity />
              </DevLayoutCharity>
            } />

            <Route path="/dev/charity/statistics/ratings" element={
              <DevLayoutCharity title="Ratings & Reviews">
                <RatingsDashboardCharity />
              </DevLayoutCharity>
            } />

            <Route path="/dev/charity/statistics/social-impact" element={
              <DevLayoutCharity title="Social Impact">
                <SocialImpactDashboardCharity />
              </DevLayoutCharity>
            } />

            <Route path="/dev/charity/statistics/analytics" element={
              <DevLayoutCharity title="Analytics">
                <AnalyticsDashboardCharity />
              </DevLayoutCharity>
            } />

            <Route path="/dev/charity/settings" element={
              <DevLayoutCharity title="Settings">
                <CharitySettings />
              </DevLayoutCharity>
            } />

            <Route path="/dev/charity/profile" element={
              <DevLayoutCharity title="Profile Settings">
                <DevProfileSettings orgType="charity" />
              </DevLayoutCharity>
            } />
            
            {/* Fallback Route */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          </LayoutProvider>
        </ThemeProvider>
      </AuthProviderV2>
    </Router>
  );
}

export default AppV2;